import { useState, useEffect, useMemo } from 'react';
import type { MockInspection } from '@/mocks/qms';
import { calcSampleQty } from '@/mocks/qms';
import { apiGetAllGRNs, apiGetGRNById } from '@/api/grn.api';
import { getAllInspectionChecklists } from '@/api/inspectionchecklist.api';
import { getAllOperators } from '@/api/operators.api';
import { getAllItems } from '@/api/item.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect from '@/components/base/SearchableSelect';

interface PrefillItem {
  itemId: string;
  itemName: string;
  qty: number;
  unit: string;
  batchNumber?: string | null;
  lotNumber?: string | null;
  qualityStatus?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inspections: MockInspection[]) => void;
  prefillGrnId?: string;
  prefillData?: {
    grnId?: string;
    grnNumber?: string;
    supplierId?: string;
    supplierName?: string;
    warehouseId?: string;
    items?: PrefillItem[];
  };
}

interface GRNItemView {
  itemId: string;
  itemName: string;
  itemCode: string;
  qty: number;
  unit: string;
  batchNumber: string | null;
  lotNumber: string | null;
}

interface ChecklistState {
  id: string;
  name: string;
  applicableTo: string;
  samplingPlan: 'ALL' | 'RANDOM_10' | 'RANDOM_20' | 'AQL';
  isActive: boolean;
  parameters: string[];
}

interface OperatorState {
  id: string;
  name: string;
  employeeCode: string;
  skill: string;
  isActive: boolean;
}

interface ItemState {
  id: string;
  code: string;
  isVariant: boolean;
  variantName: string | null;
  requiresIncomingQC: boolean;
}

interface GRNState {
  id: string;
  grnNumber: string;
  supplierId: string;
  supplierName: string;
  date: string;
  warehouseId: string;
  items: {
    itemId: string;
    itemName: string;
    itemCode: string;
    qty: number;
    unit: string;
    batchNumber: string | null;
    lotNumber: string | null;
    requiresIncomingQC: boolean;
  }[];
}

export default function NewInspectionSlideOver({ isOpen, onClose, onSave, prefillGrnId, prefillData }: Props) {
  const { selectedWarehouseId } = useWarehouseStore();
  const [grnId, setGrnId] = useState('');
  const [checklistId, setChecklistId] = useState('');
  const [samplingPlan, setSamplingPlan] = useState<'ALL' | 'RANDOM_10' | 'RANDOM_20' | 'AQL'>('ALL');
  const [inspectorId, setInspectorId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Master data states
  const [grns, setGrns] = useState<GRNState[]>([]);
  const [checklists, setChecklists] = useState<ChecklistState[]>([]);
  const [operators, setOperators] = useState<OperatorState[]>([]);
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);

  // Multi-item selection state
  const [grnItems, setGrnItems] = useState<GRNItemView[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [inspectQty, setInspectQty] = useState<Record<string, number>>({});

  // Fetch master data from APIs
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    async function loadData() {
      try {
        setLoading(true);
        const [grnsRes, checklistsRes, operatorsRes, itemsRes] = await Promise.all([
          apiGetAllGRNs(),
          getAllInspectionChecklists(),
          getAllOperators(),
          getAllItems(selectedWarehouseId || undefined),
        ]);
        if (!active) return;

        if (grnsRes.success && grnsRes.data) {
          const rawGrns = selectedWarehouseId && selectedWarehouseId !== 'ALL'
            ? grnsRes.data.filter((g: any) => !g.warehouseId || g.warehouseId === selectedWarehouseId)
            : grnsRes.data;
          const mappedGrns: GRNState[] = rawGrns.map((g: any) => ({
            id: g.id,
            grnNumber: g.grnNumber,
            supplierId: g.supplierId || '',
            supplierName: g.supplierName || '',
            date: g.date,
            warehouseId: g.warehouseId,
            items: (g.items || []).map((i: any) => ({
              itemId: i.itemId || '',
              itemName: i.itemName || '',
              itemCode: i.hsnCode || '',
              qty: i.quantity,
              unit: i.unitName || 'Pcs',
              batchNumber: i.barcode || null,
              lotNumber: i.companyBarcode || null,
              requiresIncomingQC: Boolean(i.requiresIncomingQC ?? i.requires_incoming_qc),
            })),
          }));
          setGrns(mappedGrns);
        }

        if (checklistsRes.success && checklistsRes.data) {
          const mappedChecklists: ChecklistState[] = checklistsRes.data.map((c) => ({
            id: c.id,
            name: c.name,
            applicableTo: c.applicable_to,
            samplingPlan: c.sampling_plan as ChecklistState['samplingPlan'],
            isActive: c.is_active,
            parameters: c.parameter_ids || [],
          }));
          setChecklists(mappedChecklists);
        }

        if (operatorsRes.success && operatorsRes.data) {
          const mappedOperators: OperatorState[] = operatorsRes.data.map((o) => ({
            id: o.id,
            name: o.name,
            employeeCode: o.employee_code || '',
            skill: o.skill,
            isActive: o.is_active,
          }));
          setOperators(mappedOperators);
        }

        if (itemsRes.success && itemsRes.data) {
          const mappedItems: ItemState[] = itemsRes.data.map((i: any) => ({
            id: i.id,
            code: i.code || '',
            isVariant: false,
            variantName: null,
            requiresIncomingQC: Boolean(i.requires_incoming_qc ?? i.requiresIncomingQC),
          }));
          setItems(mappedItems);
        }
      } catch (err) {
        console.error('Failed to load master data in NewInspectionSlideOver:', err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, [isOpen]);

  // Helper to determine if an item requires QC
  const isQCRequired = useCallback(
    (itemId: string, explicitFlag?: boolean) => {
      if (explicitFlag !== undefined) return Boolean(explicitFlag);
      const found = items.find((it) => it.id === itemId);
      return found ? Boolean(found.requiresIncomingQC) : false;
    },
    [items]
  );

  // Set grnId from simple prefill prop
  useEffect(() => {
    if (prefillGrnId && isOpen) {
      setGrnId(prefillGrnId);
    }
  }, [prefillGrnId, isOpen]);

  // Prefill items from navigation state
  useEffect(() => {
    if (prefillData?.items && prefillData.items.length > 0 && isOpen) {
      if (prefillData.grnId) setGrnId(prefillData.grnId);

      // Strictly filter to ONLY items that require incoming QC!
      const qcItems = prefillData.items.filter((i: any) => {
        if (i.requiresIncomingQC !== undefined) return Boolean(i.requiresIncomingQC);
        if (i.requires_incoming_qc !== undefined) return Boolean(i.requires_incoming_qc);
        return isQCRequired(i.itemId);
      });

      setGrnItems(
        qcItems.map((i) => ({
          itemId: i.itemId,
          itemName: i.itemName,
          itemCode: (i as any).itemCode || '',
          qty: i.qty,
          unit: i.unit,
          batchNumber: i.batchNumber ?? null,
          lotNumber: i.lotNumber ?? null,
        }))
      );
      setSelectedItems(qcItems.map((i) => i.itemId));
      setInspectQty(
        Object.fromEntries(qcItems.map((i) => [i.itemId, i.qty]))
      );
    }
  }, [prefillData, isOpen, isQCRequired]);

  // When GRN dropdown changes (manual selection) — fetch full GRN details if items missing
  useEffect(() => {
    if (prefillData?.items && prefillData.items.length > 0) return;
    if (!grnId) {
      setGrnItems([]);
      setSelectedItems([]);
      setInspectQty({});
      return;
    }

    async function fetchGrnItems() {
      const grn = grns.find((g) => g.id === grnId);
      let itemsList: any[] = grn?.items || [];

      if (itemsList.length === 0 && grnId) {
        try {
          const singleRes = await apiGetGRNById(grnId);
          if (singleRes.success && singleRes.data?.items && singleRes.data.items.length > 0) {
            itemsList = singleRes.data.items.map((i: any) => ({
              itemId: i.itemId || i.id,
              itemName: i.itemName || 'GRN Item',
              itemCode: i.hsnCode || '',
              qty: i.quantity || 1,
              unit: i.unitName || 'Pcs',
              batchNumber: i.barcode || null,
              lotNumber: i.companyBarcode || null,
              requiresIncomingQC: Boolean(i.requiresIncomingQC ?? i.requires_incoming_qc),
            }));
          }
        } catch (err) {
          console.error('Error fetching single GRN items:', err);
        }
      }

      // Strictly filter to ONLY items that require incoming QC!
      const qcOnlyList = itemsList.filter((i: any) => {
        if (i.requiresIncomingQC !== undefined) return Boolean(i.requiresIncomingQC);
        if (i.requires_incoming_qc !== undefined) return Boolean(i.requires_incoming_qc);
        return isQCRequired(i.itemId);
      });

      const mappedViews: GRNItemView[] = qcOnlyList.map((i) => ({
        itemId: i.itemId,
        itemName: i.itemName,
        itemCode: i.itemCode || '',
        qty: i.qty,
        unit: i.unit,
        batchNumber: i.batchNumber ?? null,
        lotNumber: i.lotNumber ?? null,
      }));

      setGrnItems(mappedViews);
      setSelectedItems(mappedViews.map((i) => i.itemId));
      setInspectQty(Object.fromEntries(mappedViews.map((i) => [i.itemId, i.qty])));
    }

    fetchGrnItems();
  }, [grnId, grns, prefillData, isQCRequired]);

  // Update sampling plan when checklist changes
  useEffect(() => {
    if (!checklistId) return;
    const cl = checklists.find((c) => c.id === checklistId);
    if (cl) {
      setSamplingPlan(cl.samplingPlan);
    }
  }, [checklistId, checklists]);

  const toggleAll = () => {
    if (selectedItems.length === grnItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(grnItems.map((i) => i.itemId));
    }
  };

  const toggleItem = (itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const allSelected = grnItems.length > 0 && selectedItems.length === grnItems.length;

  const incomingChecklists = useMemo(() => {
    const filtered = checklists.filter(
      (c) =>
        (!c.applicableTo ||
          c.applicableTo.toUpperCase() === 'INCOMING' ||
          c.applicableTo.toUpperCase() === 'ALL') &&
        c.isActive !== false
    );
    return filtered.length > 0 ? filtered : checklists;
  }, [checklists]);

  const qcInspectors = useMemo(() => {
    const filtered = operators.filter(
      (o) =>
        (!o.skill ||
          o.skill.toUpperCase() === 'QC_INSPECTOR' ||
          o.skill.toUpperCase().includes('QC') ||
          o.skill.toUpperCase().includes('INSPECTOR')) &&
        o.isActive !== false
    );
    return filtered.length > 0 ? filtered : operators;
  }, [operators]);

  const handleSave = () => {
    if (!grnId || !checklistId || !inspectorId) return;
    const grn = grns.find((g) => g.id === grnId);
    const checklist = checklists.find((c) => c.id === checklistId)!;
    const inspector = operators.find((o) => o.id === inspectorId)!;

    if (selectedItems.length === 0) return;
    const itemsToProcess = selectedItems;
    const newInspections: MockInspection[] = [];

    for (const itemId of itemsToProcess) {
      const item = grnItems.find((gi) => gi.itemId === itemId);
      const qty = item ? (inspectQty[itemId] ?? item.qty) : 1;
      const nextNum = Math.floor(1000 + Math.random() * 9000);
      const itemMaster = items.find((i) => i.id === itemId);
      const newInspection: MockInspection = {
        id: `insp-${Date.now()}-${itemId}`,
        inspectionNumber: `INSP-2024-00${nextNum}`,
        type: 'INCOMING',
        status: 'PENDING',
        triggeredBy: 'MANUAL',
        sourceType: 'GRN',
        sourceId: grnId,
        sourceNumber: prefillData?.grnNumber || grn?.grnNumber || grnId,
        itemId: item?.itemId || itemId,
        itemName: item?.itemName || grn?.grnNumber || 'Incoming Item',
        itemCode: item?.itemCode || null,
        isVariant: itemMaster?.isVariant ?? false,
        variantName: itemMaster?.variantName || null,
        checklistId: checklist.id,
        checklistName: checklist.name,
        samplingPlan,
        batchNumber: item?.batchNumber || null,
        lotNumber: item?.lotNumber || null,
        totalQty: qty,
        sampleQty: calcSampleQty(samplingPlan, qty),
        passedQty: 0,
        failedQty: 0,
        unit: item?.unit || 'Pcs',
        inspectorId: inspector.id,
        inspectorName: inspector.name,
        scheduledDate,
        completedDate: null,
        warehouseId: prefillData?.warehouseId || grn?.warehouseId || null,
        notes: null,
        createdAt: new Date().toISOString(),
      };
      newInspections.push(newInspection);
    }
    onSave(newInspections);
    resetForm();
  };

  const resetForm = () => {
    setGrnId('');
    setChecklistId('');
    setSamplingPlan('ALL');
    setInspectorId('');
    setScheduledDate(new Date().toISOString().split('T')[0]);
    setGrnItems([]);
    setSelectedItems([]);
    setInspectQty({});
  };

  if (!isOpen) return null;

  const isValid = Boolean(grnId && checklistId && inspectorId && selectedItems.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full shadow-xl flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Incoming Inspection</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* GRN Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source GRN <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={grnId}
              onChange={setGrnId}
              placeholder="Select Source GRN..."
              searchPlaceholder="Search by GRN number, supplier..."
              options={grns.map((g) => ({
                value: g.id,
                label: g.grnNumber,
                sublabel: `${g.supplierName} (${g.date})`,
                badge: `${g.items.length} items`,
                icon: 'ri-file-list-3-line',
              }))}
            />
          </div>

          {/* Alert if GRN is selected but has zero QC-required items */}
          {grnId && grnItems.length === 0 && !loading && (
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2.5">
              <i className="ri-information-line text-amber-600 text-base shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">No QC-Required Items in this GRN</p>
                <p className="text-[11px] text-amber-700 mt-0.5 leading-relaxed">
                  All items received under this GRN are Non-QC items (Direct to Stock). No incoming quality inspection is needed.
                </p>
              </div>
            </div>
          )}

          {/* Item Selection Table */}
          {grnItems.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="cursor-pointer"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Item</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Received Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Inspect Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {grnItems.map((item) => (
                    <tr key={item.itemId}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.itemId)}
                          onChange={() => toggleItem(item.itemId)}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-sm text-gray-800">
                        {item.itemName}
                        {item.itemCode && (
                          <span className="block text-xs text-gray-400">{item.itemCode}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-sm">
                        {item.qty} {item.unit}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={inspectQty[item.itemId] ?? item.qty}
                          onChange={(e) =>
                            setInspectQty((prev) => ({
                              ...prev,
                              [item.itemId]: Number(e.target.value),
                            }))
                          }
                          min={1}
                          max={item.qty}
                          disabled={!selectedItems.includes(item.itemId)}
                          className={`w-20 border rounded px-2 py-1 text-sm ${
                            selectedItems.includes(item.itemId)
                              ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500'
                              : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                          }`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500">
                {selectedItems.length} of {grnItems.length} items selected for inspection
              </div>
            </div>
          )}

          {/* Checklist */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Checklist <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={checklistId}
              onChange={setChecklistId}
              placeholder="Select Inspection Checklist..."
              searchPlaceholder="Search checklist by name..."
              options={incomingChecklists.map((c) => ({
                value: c.id,
                label: c.name,
                sublabel: `${c.parameters.length} parameters`,
                badge: c.samplingPlan,
                icon: 'ri-task-line',
              }))}
            />
          </div>

          {/* Sampling Plan */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sample Plan</label>
              <SearchableSelect
                value={samplingPlan}
                onChange={(val) => setSamplingPlan(val as typeof samplingPlan)}
                options={[
                  { value: 'ALL', label: '100% (All)' },
                  { value: 'RANDOM_10', label: 'Random 10%' },
                  { value: 'RANDOM_20', label: 'Random 20%' },
                  { value: 'AQL', label: 'AQL' },
                ]}
                placeholder="Select Sample Plan..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sample Qty</label>
              <input
                type="text"
                value="Auto per item"
                readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-400 mt-1">Calculated per item from plan</p>
            </div>
          </div>

          {/* Inspector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inspector <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={inspectorId}
              onChange={setInspectorId}
              placeholder="Select Operator / Inspector..."
              searchPlaceholder="Search by operator name or employee code..."
              options={qcInspectors.map((o) => ({
                value: o.id,
                label: o.name,
                sublabel: o.employeeCode || o.skill,
                badge: o.skill || undefined,
                icon: 'ri-user-settings-line',
              }))}
            />
          </div>

          {/* Scheduled Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white cursor-pointer whitespace-nowrap ${
              isValid ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Create{' '}
            {selectedItems.length > 0
              ? `${selectedItems.length} Inspection${selectedItems.length > 1 ? 's' : ''}`
              : 'Inspection'}
          </button>
        </div>
      </div>
    </div>
  );
}